import { ChangeDetectorRef, Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { VmsSubmittalPage } from "../../../enums";
import { PageService } from "../shared/page-service";
import { TalentHappyPathDataService } from "../../shared/service/talent-happy-path-data-service";
import { ActivatedRoute, Router } from "@angular/router";
import { TalentHappyPathAuthService } from "../../shared/service/talent-happy-path-auth.service";
import { QuickApplyApiService } from "../../quick-apply/quick-apply-api.service";
import { TalentHappyPathAuthApiService } from "../../shared/service/talent-happy-path-auth-api.service";
import { VmsSubmittalApiService } from "../vms-submittal-api.service";
import { ClientVMSSubInfo } from "../../../../typings";
import { GatherApiService } from "../../../gather/gather-services/gather-api.service";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-vms-submittal-container',
  templateUrl: './vms-submittal-container.component.html',
  styleUrl: './vms-submittal-container.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VmsSubmittalContainerComponent implements OnDestroy {
  VmsSubmittalPage = VmsSubmittalPage;
  activePage = VmsSubmittalPage.RIGHT_TO_REPRESENT;
  totalPages = 0;
  postingId: string;
  talent: Talent;
  jobTitle: string = '';
  pageLoading: boolean = false;
  clientVMSSubInfo: ClientVMSSubInfo;
  gatherUUID: string;
  private destroy$ = new Subject<void>();

  constructor(
    private pageService: PageService,
    private router: Router,
    private route: ActivatedRoute,
    private quickApplyService: QuickApplyApiService,
    private vmsSubApiService: VmsSubmittalApiService,
    private talentHappyPathDataService: TalentHappyPathDataService,
    private authAPIService: TalentHappyPathAuthApiService,
    private authService: TalentHappyPathAuthService,
    private gatherApiService: GatherApiService,
    private cdr: ChangeDetectorRef
  ) {
    this.route.queryParams.subscribe(params => {
      if (params['gatheruuid']) {
        this.gatherUUID = params['gatheruuid'];
      }
    });
  }

  ngOnInit() {
    this.pageLoading = true;
    this.authService.reload();
    this.talentHappyPathDataService.urlHasPostingId = this.route.snapshot.paramMap.has('postingId');
    if (this.talentHappyPathDataService.urlHasPostingId) {
      this.postingId = String(this.route.snapshot.paramMap.get('postingId'));
      this.quickApplyService.readPostingInfo(this.postingId).subscribe(posting => {
        if (posting) {
          this.talentHappyPathDataService.postingId = posting.jobPostingId;
          this.talentHappyPathDataService.postingInfo = posting;
          this.jobTitle = posting.jobTitle;
          this.talentHappyPathDataService.orderId = posting.orderId;
          this.vmsSubApiService.getClientVMSSubInfo(posting.orderId).subscribe(
            clientVMSInfo => {
              this.clientVMSSubInfo = clientVMSInfo;

              //If client does not have vms requirement, redirect accordingly
              if (!this.clientHasVmsRequirements(clientVMSInfo)) {
                if (this.isFromGather()) {
                  this.router.navigateByUrl('/initial-interview?gatheruuid=' + this.gatherUUID, { skipLocationChange: true });
                } else {
                  this.goToQuickApply(false);
                }
              }

              //Check for Gather
              if (this.gatherUUID && this.gatherUUID != '') {
                this.gatherApiService.getGatherByToken(this.gatherUUID).subscribe(
                  gatherInfo => {
                    this.talent = {
                      email: gatherInfo.talentEmail,
                      personId: gatherInfo.talentPersonId,
                      firstName: gatherInfo.talent?.firstName,
                      lastName: gatherInfo.talent?.lastName,
                      preferredName: gatherInfo.talent?.preferredName,
                      personTypeId: undefined,
                      profileUpdated: undefined,
                      resumeUpdated: undefined,
                      resumeData: undefined,
                      termsAccepted: undefined,
                      locationUpdated: undefined,
                      smsOptIn: undefined,
                      doubleOptIn: undefined,
                      newTHPTalent: undefined,
                      geoLocation: undefined,
                      portfolioUpdated: undefined
                    };
                    this.talentHappyPathDataService.gather = gatherInfo;
                    this.talentHappyPathDataService.gatherToken = this.gatherUUID;
                    this.handleTalentDataLoaded();
                  },
                  error => {
                    this.goToQuickApply(false);
                  }
                );
              }
              //Check for authenticated THP user
              else if (this.authService.isAuthenticated()) {
                this.authAPIService.searchTalentByToken(this.authService.authentication.id_token, this.postingId).subscribe(
                  data => {
                    this.talent = data;
                    //If not a candidate, go through normal quick apply
                    if (!this.isCandidateOnOrder()) {
                      this.goToQuickApply(false);
                    }
                    this.handleTalentDataLoaded();
                  },
                  error => {
                    this.goToQuickApply(false);
                  }
                );
              } else {
                this.goToQuickApply(true);
              }
            },
            error => {
              this.goToQuickApply(false);
            }
          );
        } else {
          this.goToJobPostingClosed();
        }
      });
    } else {
      this.router.navigate(['login'], { relativeTo: this.route.parent, queryParamsHandling: 'preserve' });
    }
  }

  /**
   * Handles the workflow after talent data is loaded, updating the necessary services,
   * calculating total pages based on required steps, and setting the active page based on priority.
   *
   * The only way one of the sections can be required when a candidate has arrived from a gather is if one of the
   * sections is already chosen for VMS data collection. For example, this.clientVMSSubInfo.vmsSubGatherInfo.candidateValidationRequired
   * can only be set to true on the front end if one of the Candidate Validation checkboxes is checked.
   *
   * Making things a bit more complex, if a candidate has arrived from a gather, the VMS data collection section can be
   * skipped, even when the data is required for VMS data collection, if the clientVMSSubInfo.vmsSubGatherInfo.whatever
   * is false
   *
   * @return {void} Does not return a value. Executes logic to initialize and manage talent data workflow.
   */
  private handleTalentDataLoaded(): void {
    this.talentHappyPathDataService.talent = this.talent;
    this.pageService.activePage$
      .pipe(takeUntil(this.destroy$))
      .subscribe(page => (this.activePage = page));

    // Determine required pages based on source
    const isFromGather = this.isFromGather();

    // Determine requirements
    const requirements = {
      rightToRepresent: isFromGather
        ? this.clientVMSSubInfo.vmsSubGatherInfo.rightToRepresentRequired
        : this.clientVMSSubInfo.r2rTypeId > 0,

      candidateValidation: isFromGather
        ? this.clientVMSSubInfo.vmsSubGatherInfo.candidateValidationRequired
        : this.clientVMSSubInfo.dobRequired || this.clientVMSSubInfo.taxIdRequired,

      employmentHistory: isFromGather
        ? this.clientVMSSubInfo.vmsSubGatherInfo.employmentHistoryRequired
        : Object.values(this.clientVMSSubInfo.clientEmploymentHistory).some(value => value === true),

      relationshipDisclosure: isFromGather
        ? this.clientVMSSubInfo.vmsSubGatherInfo.relationshipDisclosureRequired
        : this.clientVMSSubInfo.relationshipDisclosureRequired,

      ernstAndYoungEmploymentHistory: isFromGather
        ? this.clientVMSSubInfo.vmsSubGatherInfo.ernstAndYoungEmploymentHistoryRequired
        : this.clientVMSSubInfo.ernstAndYoungEmploymentHistoryRequired
    };

    // Calculate total pages
    this.totalPages = (requirements.rightToRepresent ? 1 : 0) +
      (requirements.candidateValidation ? 1 : 0) +
      (requirements.employmentHistory || requirements.relationshipDisclosure || requirements.ernstAndYoungEmploymentHistory ? 1 : 0);

    // Set the active page based on display order priority: RTR > Validation > Employment
    if (requirements.rightToRepresent) {
      this.pageService.setActivePage(VmsSubmittalPage.RIGHT_TO_REPRESENT);
    } else if (requirements.candidateValidation) {
      this.pageService.setActivePage(VmsSubmittalPage.CANDIDATE_VALIDATION);
    } else if (requirements.employmentHistory || requirements.relationshipDisclosure || requirements.ernstAndYoungEmploymentHistory) {
      this.pageService.setActivePage(VmsSubmittalPage.EMPLOYMENT_HISTORY_AND_RELATIONSHIP_DISCLOSURE);
    }

    this.pageLoading = false;
    this.cdr.markForCheck();
  }

  goToJobPostingClosed() {
    this.router.navigateByUrl('/job-posting-closed', { skipLocationChange: true });
  }

  goToQuickApply(comeBackToVMS: boolean) {
    this.talentHappyPathDataService.goToVmsSub = comeBackToVMS;
    this.router.navigate(['quick-apply', this.postingId], { skipLocationChange: true });
  }

  setActivePage(page: VmsSubmittalPage) {
    this.pageService.setActivePage(page);
  }

  /**
   * Retrieves the active page number, adjusted based on the pages actually visible in the Talent's workflow. A page's
   * visibility is determined by a combination of "use if this is a Gather" booleans and Client VMS submittal requirements.
   * @return {number} The computed active page number.
   */
  getActivePageNumber(): number {
    // Determine which pages are actually visible in the flow
    const visiblePages = [];
    const isFromGather = this.isFromGather();

    // Check if each page should be included based on requirements
    if (
      (isFromGather && this.clientVMSSubInfo.vmsSubGatherInfo.rightToRepresentRequired) ||
      (!isFromGather && this.clientVMSSubInfo.r2rTypeId > 0)
    ) {
      visiblePages.push(VmsSubmittalPage.RIGHT_TO_REPRESENT);
    }

    if (
      (isFromGather && this.clientVMSSubInfo.vmsSubGatherInfo.candidateValidationRequired) ||
      (!isFromGather && (this.clientVMSSubInfo.dobRequired || this.clientVMSSubInfo.taxIdRequired))
    ) {
      visiblePages.push(VmsSubmittalPage.CANDIDATE_VALIDATION);
    }

    if (
      (isFromGather && this.clientVMSSubInfo.vmsSubGatherInfo.employmentHistoryRequired) ||
      (!isFromGather && Object.values(this.clientVMSSubInfo.clientEmploymentHistory).some(value => value === true)) ||
      (isFromGather && this.clientVMSSubInfo.vmsSubGatherInfo.relationshipDisclosureRequired) ||
      (!isFromGather && this.clientVMSSubInfo.relationshipDisclosureRequired) ||
      (isFromGather && this.clientVMSSubInfo.vmsSubGatherInfo.ernstAndYoungEmploymentHistoryRequired) ||
      (!isFromGather && this.clientVMSSubInfo.ernstAndYoungEmploymentHistoryRequired)
    ) {
      visiblePages.push(VmsSubmittalPage.EMPLOYMENT_HISTORY_AND_RELATIONSHIP_DISCLOSURE);
    }

    // Find the index of the current page in the visible pages array
    const pageIndex = visiblePages.indexOf(this.activePage);

    // Return a 1-based page number (pageIndex is 0-based)
    return pageIndex !== -1 ? pageIndex + 1 : 1;
  }

  getCompletionPercentage() {
    if (this.totalPages === 0) {
      return 0; // Avoid division by zero
    }
    return (this.getActivePageNumber() / this.totalPages) * 100;
  }

  isCandidateOnOrder() {
    return this.talent && this.talent.personId && this.talent.statusOnOrder > 0;
  }

  clientHasVmsRequirements(clientVMSInfo) {
    return (
      this.clientVMSSubInfo &&
      (clientVMSInfo.dobRequired ||
        clientVMSInfo.taxIdRequired ||
        clientVMSInfo.r2rTypeId > 0 ||
        clientVMSInfo.relationshipDisclosureRequired ||
        clientVMSInfo.ernstAndYoungEmploymentHistoryRequired ||
        Object.values(this.clientVMSSubInfo.clientEmploymentHistory).some(value => value === true))
    );
  }

  onLoadingChange(isLoading: boolean) {
    this.pageLoading = isLoading;
  }

  isFromGather(): boolean {
    return this.gatherUUID && this.gatherUUID != '';
  }

  // ngOnDestroy lifecycle hook
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
